import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react';
import type { ReactNode, ElementType } from 'react';

type TypewriterContextType = {
  register: (callback: () => void) => number;
  next: () => void;
};

type TypewriterProviderProps = {
  children: ReactNode;
};

type TypewriterProps = {
  text: string;
  as?: ElementType;
  speed?: number;
  className?: string;
  enable?: boolean;
};

export const TypewriterContext = createContext<TypewriterContextType | null>(
  null
);

export const TypewriterProvider: React.FC<TypewriterProviderProps> = ({
  children,
}) => {
  const step = useRef(0);
  const listeners = useRef<Array<() => void>>([]);

  const register = (callback: () => void): number => {
    listeners.current.push(callback);
    return listeners.current.length - 1; // return index (order)
  };

  const next = () => {
    step.current += 1;
    const listener = listeners.current[step.current];
    if (listener) listener(); // activate the next typewriter
  };

  return (
    <TypewriterContext.Provider value={{ register, next }}>
      {children}
    </TypewriterContext.Provider>
  );
};

const useTypewriterContext = (): TypewriterContextType => {
  const ctx = useContext(TypewriterContext);
  if (!ctx) {
    throw new Error('Typewriter must be used within TypewriterProvider');
  }
  return ctx;
};

export const Typewriter: React.FC<TypewriterProps> = ({
  text,
  as: Tag = 'p',
  speed = 20,
  className = '',
  enable = true,
}) => {
  const { register, next } = useTypewriterContext();
  const [index, setIndex] = useState(0);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const order = register(() => setActive(true));
    if (order === 0) setActive(true); // first one starts automatically
  }, [register]);

  useEffect(() => {
    if (!enable) {
      setIndex(text.length);
      return;
    }

    if (!active) return;
    if (index < text.length) {
      const timeout = setTimeout(() => {
        setIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timeout);
    } else {
      next(); // trigger the next typewriter when finished
    }
  }, [active, index, text, speed, next, enable]);

  return <Tag className={`${className}`}>{text.slice(0, index)}</Tag>;
};
